// src/utils/streakFreeze.js
// Helpers do sistema de congelamento (freeze) de ofensiva — estilo Duolingo.

import {
  currentMonthKeySP,
  daysSinceDateSP,
  spDateKey,
  todayKeySP,
  yesterdayKeySP,
} from "./streakStatus.js";

// Dica local (grátis: 2 por mês — planoConfig.GAMIFICACAO.CONGELAMENTOS no
// servidor). A regra de verdade, por tier, vem de getStatusCongelamento.
export const MAX_FREEZES_PER_MONTH = 2;

/**
 * Converte a lista FROZEN_DAYS ("YYYY-MM-DD") em índices (0-6) da semana atual
 * no fuso America/Sao_Paulo — mesma base usada na tela VITA (XpVitaCard).
 * Alimenta a WeekStreakRow / StreakScreen para desenhar o floco nos dias
 * congelados. Retorna [] quando não há dias congelados nesta semana.
 * @param {string[]|undefined|null} frozenDays  campo VITA/ROOT.FROZEN_DAYS
 * @returns {number[]} índices 0-6 (Dom..Sáb) congelados nesta semana
 */
export function frozenWeekIndices(frozenDays) {
  if (!Array.isArray(frozenDays) || !frozenDays.length) return [];
  const spNow = new Date(new Date().toLocaleString("en-US", { timeZone: "America/Sao_Paulo" }));
  const dayOfWeek = spNow.getDay();
  const weekKeys = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(spNow);
    d.setDate(spNow.getDate() - dayOfWeek + i);
    return spDateKey(d);
  });
  const frozen = new Set(frozenDays);
  const out = [];
  weekKeys.forEach((key, idx) => { if (frozen.has(key)) out.push(idx); });
  return out;
}

/**
 * Quantos congelamentos o usuário ainda tem: cota do mês calendário (zera
 * automaticamente quando FREEZE_MONTH != mês atual) + saldo COMPRADO
 * (TICKETS_SALDO/CONGELAMENTO_OFENSIVA — Pacote de Tickets Extra, não expira).
 * O server já CONSOME os comprados quando a cota esgota (xpVita.js); este
 * helper só espelha na exibição. `compradoSaldo` vem de useCompradoSaldo().
 * @param {object|null} vitaData  documento VITA/ROOT (vitaData do UserContext)
 * @param {number} [compradoSaldo]  COMPRADO_SALDO de CONGELAMENTO_OFENSIVA
 * @returns {number}
 */
export function freezesRemaining(vitaData, compradoSaldo = 0) {
  const comprado = Number(compradoSaldo) > 0 ? Number(compradoSaldo) : 0;
  if (!vitaData) return comprado;
  const used = vitaData.FREEZE_MONTH === currentMonthKeySP()
    ? Number(vitaData.FREEZES_USED_THIS_MONTH) || 0
    : 0;
  return Math.max(0, MAX_FREEZES_PER_MONTH - used) + comprado;
}

/**
 * Decide se devemos oferecer o congelamento de ofensiva agora.
 *
 * Condições (todas verdadeiras):
 *   • há ofensiva ativa (streak > 0)
 *   • a última atividade foi há EXATAMENTE 2 dias → ontem foi perdido
 *   • ontem ainda não está congelado
 *   (ter ou não congelamento NÃO decide mais: o modal trata o caso sem recursos)
 *   • o usuário ainda não foi perguntado hoje (flag em localStorage)
 *
 * @param {object|null} vitaData  documento VITA/ROOT
 * @param {string|null} uid
 * @param {number} [compradoSaldo]  congelamentos COMPRADOS (soma na oferta)
 * @returns {null | {streak:number, freezesRemaining:number, dateToFreeze:string, askedKey:string}}
 */
export function getFreezePrompt(vitaData, uid, compradoSaldo = 0) {
  if (!vitaData || !uid) return null;

  const xp = vitaData.XP_VITA;
  if (!xp) return null;

  const streak = xp.STREAK_DIAS ?? 0;
  const last = xp.LAST_ACTIVITY_DATE;
  if (streak <= 0 || !last) return null;

  // exatamente 1 dia perdido (ontem); última atividade foi anteontem
  if (daysSinceDateSP(last) !== 2) return null;

  // Sem congelamento TAMBÉM abre: o modal (que lê a cota do servidor) oferece
  // comprar com VitaCoins ou — com o vídeo ligado — salvar assistindo a um
  // vídeo. Antes, quem ficava sem recurso nem ficava sabendo que a ofensiva
  // estava em risco. `remaining` virou só a dica inicial do modal.
  const remaining = freezesRemaining(vitaData, compradoSaldo);

  const dateToFreeze = yesterdayKeySP();

  // ontem já congelado?
  if (Array.isArray(vitaData.FROZEN_DAYS) && vitaData.FROZEN_DAYS.includes(dateToFreeze)) {
    return null;
  }

  // já perguntado hoje?
  const askedKey = `lesvita:freeze-asked:${uid}:${todayKeySP()}`;
  try {
    if (localStorage.getItem(askedKey) === "1") return null;
  } catch {
    /* localStorage indisponível — segue e oferece */
  }

  return { streak, freezesRemaining: remaining, dateToFreeze, askedKey };
}
